import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import AdminDashboard from '../../Admin/Dashboard/Dashboard';
import UserDetailModal from "./CustomerModel";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BASE_URL } from '../../Api/ApiUrls';
import { AuthContext } from './../../AuthContext/AuthContext';

const Customer = () => {
  const [users, setUsers] = useState([]);
  const { userId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [openModal, setOpenModal] = useState(false);

 useEffect(() => {
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/users/`);

      const today = new Date();
      
      const filteredUsers = (res.data || [])
        .filter(user => user?.role === "1")
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

const transformedUsers = filteredUsers.map((user) => {
  const subPlans = user.user_sub_plan || [];
  const properties = user.user_properties || [];
  const today = new Date();

  // Only ACTIVE PLANS
  const activePlans = subPlans.filter(
    (plan) =>
      plan.status === true && new Date(plan.expired_date) > today
  );

  // Show all active plans
  const subPlanUserType = activePlans.length
    ? activePlans
        .map((plan) => {
          const count = plan.no_of_properties_unlimited
            ? "Unlimited"
            : plan.no_of_properties ?? 0;
          return `${plan.plan_name} - ${plan.user_type} (${count})`;
        })
        .join(", ")
    : "-";

  // Show active plan properties only
  const activePlan = activePlans[0] || null;

  const subPlanProperties = activePlan
    ? activePlan.no_of_properties_unlimited
      ? "Unlimited"
      : activePlan.no_of_properties ?? "-"
    : "-";

  // Property type summary
  const propertyTypeSummary =
    properties.length > 0
      ? Object.entries(
          properties.reduce((acc, prop) => {
            const type = prop.type || "Unknown";
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {})
        )
          .map(([type, count]) => `${type} (${count})`)
          .join(", ")
      : "-";

  return {
    id: user.user_id,
    name: user.username || "-",
    mobile: user.mobile_no || "-",
    email: user.email || "-",
    address: `${user.city || "-"}, ${user.state || "-"}`,
    status: activePlans.length ? "Active" : "Inactive",

    user_sub_plan_count: subPlans.length,
    user_addon_plan_count: user.user_addon_plan?.length || 0,
    user_features_count: user.user_features?.length || 0,
    user_properties_count: properties.length,
    user_cart_items: user.user_cart_items?.length || 0,

    sub_plan_user_type: subPlanUserType,
    sub_plan_no_of_properties: subPlanProperties,
    property_type_summary: propertyTypeSummary,
  };
});


      setUsers(transformedUsers);
      console.log("Users fetched and processed successfully:", transformedUsers);

    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchUsers();
}, []);


  const handleSearchChange = (event) => setSearch(event.target.value);

  const handleView = (user) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`${BASE_URL}/users/${userId}/`);
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        alert("User deleted successfully");
      } catch (error) {
        console.error("Failed to delete user:", error);
        alert("Failed to delete user");
      }
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.mobile.includes(search) ||
      user.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleExport = () => {
    if (filteredUsers.length === 0) {
      alert("No data to export!");
      return;
    }

    const dataToExport = filteredUsers.map((user, index) => ({
      "S.No": index + 1,
      Name: user.name,
      Mobile: user.mobile,
      Email: user.email,
      Address: user.address,
      Status: user.status,
      "User Type": user.sub_plan_user_type,
      "No. of Properties": user.sub_plan_no_of_properties,
      "Property Types": user.property_type_summary,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "Customers.xlsx");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <AdminDashboard />

      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 1,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Customers Table
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            id="search"
            label="Search by Name / Mobile / Email"
            variant="outlined"
            value={search}
            onChange={handleSearchChange}
            placeholder="Type name, mobile number, or email..."
            size="small"
            sx={{ width: 300 }}
          />
          <Button variant="contained" color="success" onClick={handleExport}>
            Export
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "ID",
                  "Name",
                  "Mobile",
                  "Email",
                  "Address",
                  // "Status",
                  "User Type",
                  // "No. of Properties",
                  // "Addon Plans",
                  "Property Types",
                  "Wishlist Items",
                  "Actions",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      backgroundColor: "#1976d2",
                      color: "white",
                      textAlign: "center",
                      fontSize: 16,
                      fontWeight: "bold",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredUsers.map((user, index) => (
                <TableRow key={user.id}>
                  <TableCell sx={{ textAlign: "center" }}>{index + 1}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{user.name}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{user.mobile}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{user.email}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{user.address}</TableCell>
                  {/* <TableCell sx={{ textAlign: "center" }}>{user.status}</TableCell> */}
                  <TableCell sx={{ textAlign: "center", width: "350px" }}>
                    {user.sub_plan_user_type}
                  </TableCell>

                  {/* <TableCell sx={{ textAlign: "center" }}>
                    {user.user_addon_plan_count}
                  </TableCell> */}
                  <TableCell sx={{ textAlign: "center" }}>
                    {user.property_type_summary}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {user.user_cart_items}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton
                        color="info"
                        size="small"
                        onClick={() => handleView(user)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(user.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <UserDetailModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          user={selectedUser}
        />
      </Box>
    </Container>
  );
};

export default Customer;
